import { Options, Vue } from 'vue-class-component';
import { PropType } from 'vue';
import { mapState } from 'vuex';
import UsabillaInPageComponent from '@/sharedcomponents/pageComponents/usabillaInPageComponent/UsabillaInPageComponent.vue';
import { TravelSettings } from './TravelSettings';
import { TravelModel, STEPS } from './TravelModel';
import TravelCalculator from './TravelCalculator';
import ChoosePackageStepComponent from '../commonSteps/ChoosePackageStepComponent.vue';
import ContactInformationStepComponent from '../commonSteps/ContactInformationStepComponent.vue';
import OverviewStepComponent from '../commonSteps/OverviewStepComponent.vue';
import AdditionalInfoStepComponent from '../commonSteps/AdditionalInfoStepComponent.vue';
import PersonInfoStepComponent from './steps/PersonInfoStepComponent.vue';
import PaymentStepComponent from '../commonSteps/PaymentStepComponent.vue';
import ReceiptStepComponent from '../commonSteps/ReceiptStepComponent.vue';
import CampaignStepComponent from '../commonSteps/CampaignStepComponent.vue';
import { CardBlock, HtmlBlock } from '@/cms/definitions/content-types';
import store from '@/store/store';
import { CALCULATOR_LOADED, INIT_CALCULATOR, RESET_CALCULATORS } from '@/store/modules/calculatorContext';
import { UsabillaInPageBlock } from '@/cms/definitions/content-types';
import { gotoAddInsurances, nextStep, setFirstCard } from '../services/NavigationService';
import { resetSelectedCalculation, subscribeBasketChange } from '../services/CalculationService';
import {
	formatNumber,
	getShowReceipt,
	isValidAdditionalInfo,
	isValidAlmbrandProducts,
	isValidContactInfo,
	isValidPackage,
	isValidPaymentInfo,
	regExCleanInput,
	regExNumbers,
	showValidNowOption,
	Validator,
} from '../services/ValidationService';
import {
	getCardTitle,
	getSubtitle,
	isCalculatingExistingInsurance,
	setDetailsYearlyPrice,
	setGlobalPackageSums,
	setHighLights,
} from '../services/ProductService';
import { initComponent, STEP } from '../services/UiStepService';
import MapOrderDescription from './MapOrderDescription';
import { CalculatorUiComponent } from '../BuyInsuranceComponent';
import { exception } from '@/appinsights/logging/ApplicationInsightLog';

@Options({
	name: 'BuyInsuranceTravelComponent',
	props: {
		calculatorInfoBlock: Object as PropType<Array<HtmlBlock>>,
		contentBlocks: Object as PropType<Array<CardBlock>>,
		cardReceipt: Object as PropType<CardBlock>,
		settingsBlock: Object as PropType<HtmlBlock>,
		usabillaBlock: Object as PropType<UsabillaInPageBlock>,
		contentUrl: String, // Used by service to handle browser history
		headerCards: Object as PropType<Map<string, string>>,
	},
	components: {
		UsabillaInPageComponent,
		PersonInfoStepComponent,
		ChoosePackageStepComponent,
		OverviewStepComponent,
		AdditionalInfoStepComponent,
		ContactInformationStepComponent,
		PaymentStepComponent,
		ReceiptStepComponent,
		CampaignStepComponent,
	},
	computed: mapState<any>({
		model: (state) => state.calculatorContext[state.calculatorContext.active].model,
		cms: (state) => state.calculatorContext[state.calculatorContext.active].cms,
	}),
})
export default class BuyTravel extends Vue implements CalculatorUiComponent {
	contentBlocks: Array<CardBlock>;
	cardReceipt: CardBlock;
	settingsBlock: HtmlBlock;
	usabillaBlock: UsabillaInPageBlock;
	contentUrl: string; // Used by service to handle browser history
	headerCards: Map<string, string>;
	calculatorInfoBlock: HtmlBlock;

	// list and order of steps
	public steps: Array<string> = STEPS;

	public model!: TravelModel; // model from store
	public cms!: TravelSettings; // setting from store
	public calculator: TravelCalculator = null;
	public componentInit = false;
	public checkout = false;
	private mapOrderDescription: MapOrderDescription;
	private unsubScribeBasketChange: Function;

	public created() {
		this.initCreated();
	}

	private async initCreated() {
		if (store.getters.getActiveCalculator) {
			await store.dispatch(CALCULATOR_LOADED);
		} else {
			// this is first load - INIT store
			const cms = new TravelSettings(this.settingsBlock);
			const model: TravelModel = new TravelModel();
			if (cms.mockData) {
				TravelModel.mock(model);
			}
			await store.dispatch(INIT_CALCULATOR, {
				cms,
				model,
			});
		}

		if (!(await initComponent(this))) {
			return;
		}

		this.calculator = new TravelCalculator(this);

		setFirstCard(this);
	}
	public mounted(): void {
		this.unsubScribeBasketChange = subscribeBasketChange(this);
	}

	beforeUnmount() {
		if (this.unsubScribeBasketChange) {
			this.unsubScribeBasketChange();
		}
	}

	// called by service
	public addResetSubscription() {
		const unsubscribe = store.subscribeAction((action, state) => {
			if (action.type === RESET_CALCULATORS) {
				setTimeout(() => {
					this.checkout = true;
					store.dispatch(INIT_CALCULATOR, {
						cms: this.cms,
						model: new TravelModel(),
					});
					unsubscribe();
				}, 2000);
			}
		});
	}

	public get showValidNowOption() {
		return showValidNowOption(this.model, this.cms);
	}

	public async gotoCard(cardName: string): Promise<boolean> {
		if (cardName === STEP.PERSON_INFO || cardName === STEP.PACKAGE) {
			resetSelectedCalculation(this.model);
			// altid kun en risk (dummy)
			this.model.choosePackage.ownRiskId = this.model.calculation.abCalc.excessIdDefault;
		}

		if (cardName === STEP.PACKAGE) {
			const hasExistingTravelInsurance = isCalculatingExistingInsurance(
				'travel',
				'rejseforsikring',
				this.cms,
				this.model
			);
			if (hasExistingTravelInsurance) {
				this.gotoCard(STEP.PERSON_INFO);
				return;
			}

			this.calculator.setupExcessList();
			this.model.calculation.isCalculated = false;
			// check Age:
			if (!this.hasValidAge) {
				this.nextStep(STEP.PERSON_INFO);
				return false;
			}
		}

		if (cardName === STEP.PAYMENT) {
			// force user to choose yearly/monthly payment to ensure progressive steps
			this.model.choosePackage.monthYear = undefined;
			this.model.payment.userSelected = false;
		}

		this.model.currentCardName = cardName;

		if (cardName === STEP.ORDER) {
			store.state.showSpinner = true;
			if (!this.isValid(this.model.currentCardName)) {
				store.state.showSpinner = false;
				return false;
			}
			this.mapOrderDescription = new MapOrderDescription(this);
			await this.mapOrderDescription.orderByEmail();
			return false; // it's ok - just don't track in service
		}
		return true;
	}

	/**
	 * when nextbtn is clicked - check for valid
	 * @param cardName
	 */
	public nextStep(cardName: string, addToHistory = true) {
		nextStep(cardName, addToHistory, this);
	}

	public getSubtitle(cardName: string) {
		return getSubtitle(cardName, this.model);
	}

	public isActiveCard(cardName: string): boolean {
		return cardName === this.model.currentCardName;
	}

	/* eslint-disable */
	public isValid(cardName): boolean {
		switch (cardName) {
			case STEP.PERSON_INFO: {
				const okPerson =
					this.model.personInfo.zipCode &&
					this.model.personInfo.zipName &&
					this.model.personInfo.familyMemberCount &&
					this.hasValidAge &&
					isValidAlmbrandProducts(this.model);

				if (!okPerson) {
					return okPerson;
				}
				if (this.model.personInfo.isOldestInHousehold === 'ja') {
					this.model.personInfo.customerAge = this.model.personInfo.customerAge.trim().replace(/\D/g, '');
					this.model.personInfo.subtitle = `${this.model.personInfo.customerAge} år, ${this.model.personInfo.zipCode} ${this.model.personInfo.zipName}`;
				} else {
					this.model.personInfo.oldestInHouseholdAge = regExNumbers(
						this.model.personInfo.oldestInHouseholdAge
					);
					this.model.personInfo.subtitle = `Ældste i husstand ${this.model.personInfo.oldestInHouseholdAge} år, ${this.model.personInfo.zipCode} ${this.model.personInfo.zipName}`;
				}
				this.model.personInfo.subtitle = regExCleanInput(this.model.personInfo.subtitle);
				return okPerson;
			}
			case STEP.PACKAGE:
				return isValidPackage(this) && this.model.choosePackage.segmentedProductProperty !== undefined;
			case STEP.OVERVIEW:
				return isValidPackage(this);
			case STEP.CONTACT_INFORMATION:
				return isValidContactInfo(this.model, this.cms);
			case STEP.ADDITIONAL_INFO:
				return isValidAdditionalInfo(false, this.model, this.cms);
			case STEP.PAYMENT:
				return isValidPaymentInfo(this.model, this.cms);
			default:
				return true;
		}
	}
	/* eslint-enable */
	// called by service
	public setOverviewData() {
		setHighLights(this.model);

		const chosenAge =
			this.model.personInfo.isOldestInHousehold === 'ja'
				? this.model.personInfo.customerAge + ' år'
				: 'Ældste i husstand ' + this.model.personInfo.oldestInHouseholdAge + ' år';

		this.model.overview.details = [
			chosenAge,
			this.model.personInfo.zipCode + ', ' + this.model.personInfo.zipName,
			this.model.personInfo.familyMemberCount,
			'Destination: ' + this.model.choosePackage.segmentedProductProperty,
		];
		try {
			if (this.model.choosePackage?.selectedPackage?.expandedCoverages?.includes('Afbestilling')) {
				const sumFormatted = formatNumber.format(this.cms.cancelSum);
				this.model.overview.details.push('Afbestillingssum: ' + sumFormatted + ' kr.');
			}
		} catch (e) {
			exception(e);
			// TOO bad
		}
		setGlobalPackageSums(this.model);
		setDetailsYearlyPrice(this.model, this.cms);
	}

	/** called by service */
	public buildDescriptionForEmail(desc: string) {
		return this.mapOrderDescription.buildDescriptionForEmail(desc);
	}

	public gotoAddInsurances(step: string) {
		gotoAddInsurances(step, this);
	}

	public get showReceipt() {
		return getShowReceipt(this);
	}

	public getCardTitle(card): string {
		return getCardTitle(card, this.model, this.cms);
	}

	public get hasValidAge(): boolean {
		return (
			(this.model.personInfo.isOldestInHousehold === 'nej' &&
				Validator.isValidAge(this.model.personInfo.oldestInHouseholdAge)) ||
			(this.model.personInfo.isOldestInHousehold === 'ja' &&
				Validator.isValidAge(this.model.personInfo.customerAge))
		);
	}
}
